import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import type { paths } from '../api/schema.js';
import { LanguageButtons } from '../components/LanguageSwitch.js';
import { LogoMark, landingUrl } from '../components/Layouts.js';
import { ThemeToggle } from '../components/ThemeToggle.js';
import { Loading, Notice } from '../components/ui.js';
import {
  formatDateTime,
  formatDuration,
  formatNumber,
  formatTime,
  intlLocale,
  isLanguage,
  sameDay,
  toLanguage,
  type Language,
} from '../i18n.js';

type Page = paths['/public/status/{slug}']['get']['responses'][200]['content']['application/json'];

/** Minutes of outage from which a day counts as a real outage (red) rather than a short one. */
const LONG_OUTAGE_SECONDS = 30 * 60;

/** The visitor's language on status pages, kept in the browser like the theme (decision 13.10). */
const LANGUAGE_KEY = 'sp-status-language';

function savedLanguage(): Language | null {
  try {
    const value = localStorage.getItem(LANGUAGE_KEY);
    return isLanguage(value) ? value : null;
  } catch {
    return null; // storage blocked: the account's language
  }
}

function saveLanguage(language: Language) {
  try {
    localStorage.setItem(LANGUAGE_KEY, language);
  } catch {
    // not kept: the choice lasts for this page
  }
}

/** Bar of a day (sheet: .ticks i): no outage, short, long, not monitored yet. */
const tick = (down: number | null) =>
  down === null ? 'n' : down === 0 ? undefined : down < LONG_OUTAGE_SECONDS ? 'w' : 'b';

/** «PS» for «Praxis Sommer»: stands in for a logo (audit 12.10). */
const monogram = (name: string) =>
  name
    .split(/[\s.\-–_]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => (Array.from(word)[0] ?? '').toUpperCase())
    .join('');

/** 10.10.2026 · 10 Oct 2026: a day as in the page's dates with a time. */
const formatDay = (iso: string, language: string, timeZone: string) =>
  new Intl.DateTimeFormat(intlLocale(language), { dateStyle: 'medium', timeZone }).format(
    new Date(iso),
  );

/**
 * Public status page of a site (T-70; status.html), for its visitors: no sign-in, nothing
 * technical. In the account's language until the visitor picks one; refreshes every minute and
 * keeps what it showed when a refresh fails («Stand» tells how fresh it is).
 */
export function StatusPage() {
  const { slug = '' } = useParams();
  const { i18n } = useTranslation();
  const [chosen, setChosen] = useState<Language | null>(savedLanguage);
  const page = useQuery({
    queryKey: ['public-status', slug],
    queryFn: () => unwrap(api.GET('/public/status/{slug}', { params: { path: { slug } } })),
    refetchInterval: 60_000,
    retry: false,
  });
  const locale = chosen ?? page.data?.locale ?? toLanguage(i18n.language);
  const t = i18n.getFixedT(locale);
  // A page switched off answers 404: then it is gone, even if it was shown a minute ago.
  const notFound = page.error instanceof ApiError && page.error.code === 'NOT_FOUND';
  const name = notFound ? undefined : page.data?.name;
  useEffect(() => {
    if (notFound) document.title = t('publicStatus.notFoundTitle');
    else if (name) document.title = t('publicStatus.docTitle', { name });
  }, [notFound, name, t]);

  const brand = t('app.name');
  const home = landingUrl();
  const frame = (top: ReactNode, body: ReactNode) => (
    // The page's language for its text, so that it is read out in that language.
    <main className="status" lang={locale}>
      <div className="top">
        {top}
        <div className="acts">
          <LanguageButtons
            current={locale}
            onChange={(language) => {
              setChosen(language);
              saveLanguage(language);
            }}
            t={t}
          />
          <ThemeToggle t={t} />
        </div>
      </div>
      {body}
    </main>
  );
  const brandLink = (children: ReactNode) =>
    home ? (
      <a className="logo" href={`${home}?ref=status`}>
        {children}
      </a>
    ) : (
      <span className="logo">{children}</span>
    );

  if (page.isPending) return frame(null, <Loading t={t} />);
  if (!page.data || notFound)
    return frame(
      brandLink(
        <>
          <LogoMark />
          {brand}
        </>,
      ),
      <div className="empty">
        <h1>{notFound ? t('publicStatus.notFoundTitle') : t('publicStatus.unavailableTitle')}</h1>
        <span>{notFound ? t('publicStatus.notFound') : t('publicStatus.unavailable')}</span>
      </div>,
    );

  const p = page.data;
  const tz = p.timezone;
  /** The time alone today, with the date on other days. */
  const moment = (iso: string) =>
    sameDay(new Date(iso), new Date(), tz)
      ? formatTime(iso, locale, tz)
      : formatDateTime(iso, locale, tz);

  return frame(
    <div className="who">
      {p.logo ? (
        <img src={p.logo} alt="" />
      ) : (
        <span className="mark" aria-hidden="true">
          {monogram(p.name)}
        </span>
      )}
      <div>
        <h1>{p.name}</h1>
        <span>{t('publicStatus.subtitle')}</span>
      </div>
    </div>,
    <>
      <section className="card" aria-live="polite">
        <div className={`state ${p.status}`}>
          <span className="dot" aria-hidden="true" />
          <div>
            <b>
              {p.status === 'down' && p.since
                ? t('publicStatus.state.downSince', { time: moment(p.since) })
                : t(`publicStatus.state.${p.status}`)}
            </b>
            {p.status === 'down' ? <span>{t('publicStatus.state.downNote')}</span> : null}
          </div>
        </div>
      </section>
      {/* The owner's note in every state: during an outage it matters most (decision 13.10). */}
      {p.message ? (
        <Notice kind="info" title={t('publicStatus.message')}>
          {/* The owner writes in the account's language, whatever the visitor chose. */}
          <span lang={p.locale}>{p.message}</span>
          {p.messageAt ? (
            <small className="at">{formatDateTime(p.messageAt, locale, tz)}</small>
          ) : null}
        </Notice>
      ) : null}
      {/* Without the main monitor there are no days and no outages to show (decision 13.10). */}
      {p.days.length > 0 ? (
        <>
          <Availability page={p} locale={locale} t={t} />
          <Outages page={p} locale={locale} t={t} />
        </>
      ) : null}
      <footer>
        <span>{t('publicStatus.checkedAt', { time: moment(p.checkedAt) })}</span>
        {brandLink(
          <>
            <span>{t('publicStatus.monitoredBy')}</span>
            {' '}
            <LogoMark />
            {brand}
          </>,
        )}
      </footer>
    </>,
  );
}

/** «Verfügbarkeit der letzten 90 Tage»: a bar a day, the share reachable as the API gives it. */
function Availability({ page: p, locale, t }: { page: Page; locale: string; t: TFunction }) {
  const day = (d: string) => formatDay(`${d}T12:00:00Z`, locale, 'UTC');
  const title = (d: { day: string; downSeconds: number | null }) =>
    d.downSeconds === null
      ? t('publicStatus.dayNone', { day: day(d.day) })
      : d.downSeconds === 0
        ? t('publicStatus.dayUp', { day: day(d.day) })
        : t('publicStatus.dayDown', {
            day: day(d.day),
            duration: formatDuration(d.downSeconds, locale),
          });
  const heading = t('publicStatus.days', { count: p.days.length });
  // Two decimals as in the API: with one, 99.96 % would read as «100 %» (decision 13.10).
  const uptime =
    p.uptimePct === null
      ? null
      : t('publicStatus.uptime', { pct: formatNumber(p.uptimePct, locale, 2) });
  return (
    <section className="card avail">
      <div className="hd">
        <h2>{heading}</h2>
        {uptime ? <span className="pct">{uptime}</span> : null}
      </div>
      <div className="ticks" role="img" aria-label={uptime ?? heading}>
        {p.days.map((d) => (
          <i key={d.day} title={title(d)} className={tick(d.downSeconds)} />
        ))}
      </div>
      <div className="axis">
        {/* The first day's date: exact, where «vor 90 Tagen» would be a day off (today counts). */}
        <span>{p.days[0] ? day(p.days[0].day) : ''}</span>
        <span>{t('publicStatus.today')}</span>
      </div>
      <div className="legend">
        <span>
          <i aria-hidden="true" />
          {t('publicStatus.legendUp')}
        </span>
        <span>
          <i className="w" aria-hidden="true" />
          {t('publicStatus.legendShort')}
        </span>
        <span>
          <i className="b" aria-hidden="true" />
          {t('publicStatus.legendDown')}
        </span>
      </div>
    </section>
  );
}

/** «Letzte Ausfälle»: when each began and ended, short ones (under 30 min.) apart. */
function Outages({ page: p, locale, t }: { page: Page; locale: string; t: TFunction }) {
  const id = useId();
  const tz = p.timezone;
  return (
    <section className="card">
      <h2 id={id}>{t('publicStatus.incidents')}</h2>
      {p.incidents.length === 0 ? (
        <p className="none">{t('publicStatus.noIncidents')}</p>
      ) : (
        <ul className="outages" aria-labelledby={id}>
          {p.incidents.map((i) => {
            const end = i.resolvedAt;
            const seconds =
              end === null
                ? null
                : (i.durationSeconds ??
                  Math.round((Date.parse(end) - Date.parse(i.startedAt)) / 1000));
            const short = seconds !== null && seconds < LONG_OUTAGE_SECONDS;
            const day = formatDay(i.startedAt, locale, tz);
            const from = formatTime(i.startedAt, locale, tz);
            const when =
              end === null
                ? t('publicStatus.periodOngoing', { day, from })
                : sameDay(new Date(i.startedAt), new Date(end), tz)
                  ? t('publicStatus.period', { day, from, to: formatTime(end, locale, tz) })
                  : t('publicStatus.periodDays', {
                      from: formatDateTime(i.startedAt, locale, tz),
                      to: formatDateTime(end, locale, tz),
                    });
            return (
              <li key={i.startedAt}>
                <span className={short ? 'ico w' : 'ico'} aria-hidden="true" />
                <div>
                  <b>{short ? t('publicStatus.outageShort') : t('publicStatus.outageDown')}</b>
                  <span>{when}</span>
                </div>
                <span className="meta">
                  {seconds === null
                    ? t('publicStatus.ongoing')
                    : t('publicStatus.lasted', { duration: formatDuration(seconds, locale) })}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
